import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useNotifyIntrinsicHeight, useOpenAiState } from "./openai-hooks";
import type { GrantDetails, GrantSearchResult, ToolOutput } from "./types";

const EMPTY_STATE = "Describe the grant or project you are looking for. Add optional filters to refine.";

export default function App() {
  const { toolOutput, toolResponseMetadata, widgetState, updateWidgetState, api } = useOpenAiState();
  const [queryText, setQueryText] = useState("");
  const [deadline, setDeadline] = useState("");
  const [status, setStatus] = useState("any");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [results, setResults] = useState<GrantSearchResult[]>([]);
  const [details, setDetails] = useState<GrantDetails | null>(null);
  const [rawText, setRawText] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!toolOutput || typeof toolOutput !== "object") {
      return;
    }

    const output = toolOutput as ToolOutput;

    if (output.type === "grant_search_results") {
      setResults(output.results ?? []);
      setDetails(null);
      setRawText("");
      setErrorMessage(null);
      setIsSearching(false);
      const shouldUpdate =
        widgetState.lastQuery !== output.query || widgetState.selectedId !== undefined;
      if (shouldUpdate) {
        updateWidgetState({
          ...widgetState,
          lastQuery: output.query,
          selectedId: undefined
        });
      }
    }

    if (output.type === "grant_details") {
      setDetails(output.grant);
      setErrorMessage(null);
      setIsFetching(false);
      const metadata = toolResponseMetadata ?? {};
      setRawText(typeof metadata.rawText === "string" ? metadata.rawText : "");
    }

    if (output.type === "error") {
      setErrorMessage(output.message);
      setIsSearching(false);
      setIsFetching(false);
    }
  }, [toolOutput, toolResponseMetadata, updateWidgetState, widgetState]);

  useNotifyIntrinsicHeight([results.length, details?.id, widgetState.showFullText]);

  const sortedResults = useMemo(() => {
    const list = [...results];
    if (widgetState.sort === "title" || widgetState.sort === undefined) {
      return list.sort((a, b) => a.title.localeCompare(b.title));
    }
    if (widgetState.sort === "deadline") {
      return list.sort((a, b) => (a.deadline ?? "").localeCompare(b.deadline ?? ""));
    }
    return list;
  }, [results, widgetState.sort]);

  const synthesizedQuery = useMemo(() => {
    const parts: string[] = [];
    if (queryText.trim()) {
      parts.push(queryText.trim());
    }
    if (status === "open") {
      parts.push("status open");
    }
    if (deadline) {
      parts.push(`deadline after ${deadline}`);
    }
    if (budgetMin || budgetMax) {
      const min = budgetMin || "0";
      const max = budgetMax || "";
      parts.push(`budget ${min}${max ? `-${max}` : "+"}M`);
    }
    return parts.join(", ");
  }, [budgetMax, budgetMin, deadline, queryText, status]);

  const sanitizedHtml = useMemo(() => {
    if (!rawText) {
      return "";
    }
    return DOMPurify.sanitize(rawText, { USE_PROFILES: { html: true } });
  }, [rawText]);

  const showFullText = widgetState.showFullText ?? false;
  const previewText = rawText.slice(0, 900);
  const hasHtml = /<[a-z][\s\S]*>/i.test(rawText);

  const handleSearch = async () => {
    if (!synthesizedQuery) {
      setErrorMessage("Please enter a search description.");
      return;
    }
    setIsSearching(true);
    setErrorMessage(null);
    updateWidgetState({ ...widgetState, lastQuery: synthesizedQuery });
    await api?.callTool?.("search", { query: synthesizedQuery });
  };

  const handleSelect = async (item: GrantSearchResult) => {
    updateWidgetState({ ...widgetState, selectedId: item.id });
    setIsFetching(true);
    await api?.callTool?.("fetch", { id: item.id });
  };

  const handleCopyFacts = async () => {
    if (!details) {
      return;
    }
    const summary = [
      `Title: ${details.title}`,
      `ID: ${details.id}`,
      details.metadata?.deadline ? `Deadline: ${details.metadata.deadline}` : null,
      details.metadata?.status ? `Status: ${details.metadata.status}` : null,
      `URL: ${details.url}`
    ]
      .filter(Boolean)
      .join("\n");

    try {
      await navigator.clipboard.writeText(summary);
    } catch (err) {
      console.warn("Failed to copy", err);
    }
  };

  return (
    <div className="min-h-screen p-6 text-ink-900 dark:text-sand-50">
      <div className="glass-panel rounded-3xl border border-sand-200/60 dark:border-white/10 shadow-xl p-6">
        <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-ink-300">Grant Finder</p>
            <h1 className="font-display text-3xl font-semibold">Find the next funding opportunity.</h1>
          </div>
          <div className="rounded-full border border-moss-400/40 bg-white/70 dark:bg-black/30 px-4 py-2 text-xs text-ink-500 dark:text-sand-100">
            Powered by MCP + Apps SDK
          </div>
        </header>

        <section className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <label className="text-sm font-medium text-ink-500 dark:text-sand-200">
              Describe your grant needs
            </label>
            <textarea
              value={queryText}
              onChange={(event) => setQueryText(event.target.value)}
              rows={4}
              className="w-full rounded-2xl border border-sand-200/80 bg-white/80 p-4 text-sm shadow-sm focus:border-moss-400 focus:outline-none dark:border-white/10 dark:bg-white/5"
              placeholder="e.g. Horizon Europe grants about space refuelling and orbital logistics"
            />
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-wider text-ink-300">Deadline after</label>
                <input
                  type="date"
                  value={deadline}
                  onChange={(event) => setDeadline(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-sand-200/80 bg-white/80 p-2 text-sm focus:border-moss-400 focus:outline-none dark:border-white/10 dark:bg-white/5"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink-300">Status</label>
                <select
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-sand-200/80 bg-white/80 p-2 text-sm focus:border-moss-400 focus:outline-none dark:border-white/10 dark:bg-white/5"
                >
                  <option value="any">Any</option>
                  <option value="open">Open</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink-300">Budget min (M)</label>
                <input
                  type="number"
                  value={budgetMin}
                  onChange={(event) => setBudgetMin(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-sand-200/80 bg-white/80 p-2 text-sm focus:border-moss-400 focus:outline-none dark:border-white/10 dark:bg-white/5"
                  placeholder="2"
                  min={0}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink-300">Budget max (M)</label>
                <input
                  type="number"
                  value={budgetMax}
                  onChange={(event) => setBudgetMax(event.target.value)}
                  className="mt-2 w-full rounded-xl border border-sand-200/80 bg-white/80 p-2 text-sm focus:border-moss-400 focus:outline-none dark:border-white/10 dark:bg-white/5"
                  placeholder="10"
                  min={0}
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="rounded-full bg-moss-500 px-5 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-moss-400 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSearching ? "Searching..." : "Search grants"}
              </button>
              <span className="text-xs text-ink-300">{synthesizedQuery || EMPTY_STATE}</span>
            </div>
            {errorMessage ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 dark:border-red-400/40 dark:bg-red-500/10">
                {errorMessage}
              </div>
            ) : null}
          </div>

          <div className="rounded-3xl border border-sand-200/80 bg-white/80 p-4 shadow-sm dark:border-white/10 dark:bg-white/5">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg">Results</h2>
              <div className="flex items-center gap-2 text-xs text-ink-300">
                <span>Sort</span>
                <button
                  onClick={() => updateWidgetState({ ...widgetState, sort: "title" })}
                  className={`rounded-full px-3 py-1 ${
                    widgetState.sort !== "deadline" ? "bg-sand-200/80 text-ink-500" : "bg-transparent"
                  }`}
                >
                  Title
                </button>
                <button
                  onClick={() => updateWidgetState({ ...widgetState, sort: "deadline" })}
                  className={`rounded-full px-3 py-1 ${
                    widgetState.sort === "deadline" ? "bg-sand-200/80 text-ink-500" : "bg-transparent"
                  }`}
                >
                  Deadline
                </button>
              </div>
            </div>
            <div className="mt-3 max-h-[260px] space-y-2 overflow-y-auto pr-1 grant-scrollbar">
              {sortedResults.length === 0 ? (
                <p className="text-sm text-ink-300">No results yet. Run a search to populate this list.</p>
              ) : null}
              {sortedResults.map((item) => {
                const isSelected = widgetState.selectedId === item.id;
                return (
                  <div
                    key={item.id}
                    className={`cursor-pointer rounded-2xl border p-3 transition ${
                      isSelected
                        ? "border-moss-400 bg-moss-500/10"
                        : "border-sand-200/80 bg-white/70 hover:border-moss-400/60 dark:border-white/10"
                    }`}
                    onClick={() => handleSelect(item)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-ink-700 dark:text-sand-50">{item.title}</p>
                        <p className="text-xs text-ink-300">{item.id}</p>
                      </div>
                      <button
                        className="text-xs text-moss-600 hover:text-moss-400"
                        onClick={(event) => {
                          event.stopPropagation();
                          api?.openExternal?.({ href: item.url });
                        }}
                      >
                        Open link
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="mt-6 rounded-3xl border border-sand-200/80 bg-white/80 p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-ink-300">Details</p>
              <h2 className="font-display text-2xl">
                {details ? details.title : "Select a grant to view details"}
              </h2>
              {details ? (
                <p className="mt-1 text-xs text-ink-300">{details.id}</p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {details?.metadata?.deadline ? (
                <span className="rounded-full bg-sand-200 px-3 py-1 text-xs text-ink-500">
                  Deadline: {details.metadata.deadline}
                </span>
              ) : null}
              {details?.metadata?.status ? (
                <span className="rounded-full bg-sand-200 px-3 py-1 text-xs text-ink-500">
                  Status: {details.metadata.status}
                </span>
              ) : null}
              {details?.metadata?.start_date ? (
                <span className="rounded-full bg-sand-200 px-3 py-1 text-xs text-ink-500">
                  Start: {details.metadata.start_date}
                </span>
              ) : null}
              {details?.metadata?.call_identifier ? (
                <span className="rounded-full bg-sand-200 px-3 py-1 text-xs text-ink-500">
                  Call: {details.metadata.call_identifier}
                </span>
              ) : null}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              className="rounded-full border border-moss-400 px-4 py-2 text-xs font-semibold text-moss-600 hover:bg-moss-500/10"
              disabled={!details?.metadata?.eu_funding_url}
              onClick={() =>
                details?.metadata?.eu_funding_url &&
                api?.openExternal?.({ href: details.metadata.eu_funding_url })
              }
            >
              Open EU Portal
            </button>
            <button
              className="rounded-full border border-moss-400 px-4 py-2 text-xs font-semibold text-moss-600 hover:bg-moss-500/10"
              disabled={!details?.url}
              onClick={() => details?.url && api?.openExternal?.({ href: details.url })}
            >
              Open Grant Page
            </button>
            <button
              className="rounded-full border border-ink-300 px-4 py-2 text-xs font-semibold text-ink-500 hover:bg-ink-300/10"
              disabled={!details}
              onClick={handleCopyFacts}
            >
              Copy key facts
            </button>
            {isFetching ? <span className="text-xs text-ink-300">Loading details...</span> : null}
          </div>

          <div className="mt-4 rounded-2xl border border-sand-200/80 bg-sand-50/80 p-4 text-sm text-ink-700 dark:border-white/10 dark:bg-black/30 dark:text-sand-50">
            {!details ? (
              <p>Choose a result to see the full grant description.</p>
            ) : rawText ? (
              <div className="space-y-3">
                {!showFullText ? (
                  <p className="whitespace-pre-wrap">{previewText}...</p>
                ) : hasHtml ? (
                  <div
                    className="max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                  />
                ) : (
                  <p className="whitespace-pre-wrap">{rawText}</p>
                )}
                <button
                  className="text-xs font-semibold text-moss-600"
                  onClick={() => updateWidgetState({ ...widgetState, showFullText: !showFullText })}
                >
                  {showFullText ? "Collapse" : "Show full text"}
                </button>
              </div>
            ) : (
              <p>No description text available yet.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
